// services/school-ui/src/pages/promotion/PreviewPane.tsx
// Right-pane preview body. Owns the React Query for the preview
// endpoint. Renders one of: loading / error / 423-ingesting /
// empty / truncated-with-content states.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist";
// Vite '?url' import bundles the worker as a separate static asset and
// gives us a usable URL we can hand to PDF.js's GlobalWorkerOptions.
// Bundling the worker locally (rather than CDN) means the embed works
// offline, in air-gapped tenants, and behind any CSP that blocks
// third-party scripts.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { StillIngestingError, fetchPreview } from "./api";
import { authHeaders } from "../../api/school";

// Chrome's dynamic-import module registry caches failed loads per
// session — a single bad MIME on first import (we shipped that in
// v42 before fixing nginx in v43) marks the URL un-importable for
// the rest of the tab's life, and reloading the SPA doesn't help
// unless the URL changes. Append a build-time cache-buster so a
// fresh build always renders a fresh import URL even when the
// content-hashed filename happens to match an older one.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  pdfWorkerUrl + (pdfWorkerUrl.includes("?") ? "&" : "?") + "v=" + __APP_VERSION__;

/** Click handler for the "Download source" button. Fetches the raw
 *  bytes via the /source endpoint (proxied through school-backend),
 *  then triggers a browser download using the Content-Disposition
 *  filename Nova OS attached. Soft-fails on error — admin can retry.
 */
async function downloadSource(requestId: string): Promise<void> {
  const r = await fetch(`/api/school/admin/promotions/${requestId}/source`, {
    headers: authHeaders(),
  });
  if (!r.ok) return;
  // Pull the filename out of Content-Disposition; fall back to a
  // generic name with the request id.
  const cd = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(cd);
  const filename = match?.[1] || `promotion-${requestId}.bin`;
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Detect whether the preview text is the binary placeholder
 *  DocumentChunkReader emits. Used to decide whether to show the
 *  "Download source" CTA prominently. */
function isBinaryPlaceholder(text: string): boolean {
  return text.startsWith("[binary file:");
}

/** PdfInlinePreview fetches the staged source bytes (auth via the
 *  same authHeaders() the rest of the app uses), then renders the PDF
 *  page-by-page into HTML canvases via PDF.js.
 *
 *  Why PDF.js rather than <object type="application/pdf"> or an
 *  iframe pointing at a blob URL: Chrome (and other Chromium-derived
 *  browsers) disable the built-in PDF viewer in Incognito mode and
 *  under various enterprise policies — both the v40 iframe and the
 *  v41 <object> fallback rendered as a black void in Charles' Incognito
 *  session. PDF.js is JS-only, so it works regardless of viewer
 *  plug-in state, CSP, or browser mode.
 *
 *  Bytes pipeline: fetch /source → ArrayBuffer → pdfjsLib.getDocument
 *  → for each page, getPage().render(canvas). The worker is bundled
 *  locally via Vite's ?url import (see the top-level import block)
 *  so the embed works offline and behind a strict CSP.
 *
 *  Page-by-page render loop walks all pages serially. For the typical
 *  ~5-50 page Bosong PDF that's fast enough; if larger decks become
 *  common we'd swap to react-window-style virtualisation.
 */
function PdfInlinePreview({
  requestId,
  sourceQuery,
}: {
  requestId: string;
  // Query string appended to /source — empty for native PDFs, "as=pdf"
  // for PPTX/DOCX so promotion-svc swaps in the LibreOffice-converted
  // sibling. Defaults to "" so existing callers don't break.
  sourceQuery?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "rendering" | "done">(
    "loading",
  );
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (container) container.innerHTML = "";

    (async () => {
      try {
        setStatus("loading");
        const sourceUrl =
          `/api/school/admin/promotions/${requestId}/source` +
          (sourceQuery ? `?${sourceQuery}` : "");
        const r = await fetch(sourceUrl, { headers: authHeaders() });
        if (!r.ok) {
          if (!cancelled) setErr(`HTTP ${r.status}`);
          return;
        }
        const buf = await r.arrayBuffer();
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        setStatus("rendering");

        // Devicepixelratio gives us crisp rendering on retina; PDF.js
        // scales the canvas internally to match.
        const dpr = window.devicePixelRatio || 1;
        // Page width = container width (minus a tiny margin). We
        // measure once up front; resizing later just means slightly
        // off-aspect pages, which is fine for a preview.
        const containerWidth = (container?.clientWidth ?? 800) - 16;

        for (let p = 1; p <= pdf.numPages; p++) {
          if (cancelled) return;
          const page = await pdf.getPage(p);
          const viewport0 = page.getViewport({ scale: 1 });
          const scale = containerWidth / viewport0.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.style.display = "block";
          canvas.style.marginBottom = "12px";
          canvas.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
          canvas.style.borderRadius = "4px";
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;

          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({
            canvasContext: ctx,
            viewport,
            transform: [dpr, 0, 0, dpr, 0, 0],
          }).promise;

          if (cancelled) return;
          container?.appendChild(canvas);
        }
        if (!cancelled) setStatus("done");
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestId, sourceQuery]);

  if (err) {
    return (
      <p className="text-sm text-red-600">
        {t("promotion.preview.failed")}: {err}
      </p>
    );
  }
  return (
    <div className="w-full max-h-[80vh] overflow-y-auto bg-slate-100 rounded p-2">
      {status === "loading" && (
        <p className="text-sm text-slate-500 italic px-2 py-1">
          {t("promotion.preview.loading")}
        </p>
      )}
      {status === "rendering" && pageCount > 0 && (
        <p className="text-[11px] text-slate-500 italic px-2 py-1">
          {t("promotion.preview.rendering", { pageCount })}
        </p>
      )}
      <div ref={containerRef} />
    </div>
  );
}

interface Props {
  requestId: string;
}

export function PreviewPane({ requestId }: Props): JSX.Element {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["promotion-preview", requestId],
    queryFn: () => fetchPreview(requestId),
    retry: (failureCount, err) => {
      // Don't retry on 403 or 423; let the UI surface the state.
      if (err instanceof StillIngestingError) return false;
      return failureCount < 1;
    },
  });

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto px-6 py-4 text-sm text-slate-500">
        {t("promotion.preview.loading")}
      </div>
    );
  }
  if (error instanceof StillIngestingError) {
    return (
      <div className="flex-1 overflow-auto px-6 py-4 text-sm italic text-slate-500">
        {t("promotion.preview.ingesting")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex-1 overflow-auto px-6 py-4">
        <p className="text-sm text-red-600">{t("promotion.preview.failed")}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50"
        >
          {t("promotion.preview.retry")}
        </button>
      </div>
    );
  }
  // The empty-state copy is the fallback when there's nothing for any
  // renderer to show. PDFs intentionally arrive with empty
  // extracted_text + no ingest_warning because the actual bytes
  // render via PdfInlinePreview's /source fetch — bail out of this
  // branch for them so the renderer below gets a chance to fire.
  if (
    !data ||
    (data.extracted_text.length === 0 &&
      !data.ingest_warning &&
      data.content_type !== "application/pdf")
  ) {
    return (
      <div className="flex-1 overflow-auto px-6 py-4 text-sm italic text-slate-500">
        {t("promotion.preview.empty")}
      </div>
    );
  }
  const isPdf = data.content_type === "application/pdf";
  // When content_type is application/pdf we always ask for ?as=pdf.
  // The promotion-svc lookup for the PDF sibling returns the same
  // file for native PDFs (sibling of foo.pdf is foo.pdf), so the
  // query is a no-op there and unlocks the converted PDF for
  // PPTX/DOCX submissions. Native PDFs and converted PDFs reach
  // the PDF.js pipeline through the same code path.
  const sourceQuery = isPdf ? "as=pdf" : "";
  const isBinary = !isPdf && isBinaryPlaceholder(data.extracted_text);
  return (
    <div className="flex-1 overflow-auto px-6 py-4">
      {/* Surface the reader's own diagnostic message at the top of the
          pane. This is how DocumentChunkReader communicates "file
          not found", "preview unauthorized", "binary file — download
          to inspect" without dragging the whole pane into an error
          state. Without this the empty + ingest_warning case
          collapsed to the generic "no text content" copy and the
          admin had no idea what went wrong. */}
      {data.ingest_warning && (
        <p className="mb-3 text-xs rounded bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2">
          {data.ingest_warning}
        </p>
      )}
      {isPdf ? (
        // PDF — embed the browser's native viewer. The download
        // button stays available as a sibling for admins who want
        // the file locally.
        <>
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={() => void downloadSource(requestId)}
              className="text-xs text-sky-700 hover:underline"
            >
              {t("promotion.preview.download")}
            </button>
          </div>
          <PdfInlinePreview requestId={requestId} sourceQuery={sourceQuery} />
        </>
      ) : isBinary ? (
        // Binary placeholder — preview can't render the file, but we
        // can hand the admin a download so they can open it in
        // PowerPoint / Reader / etc. before deciding.
        <div className="flex flex-col items-start gap-3 rounded border border-slate-200 bg-slate-50 px-4 py-4">
          <pre className="whitespace-pre-wrap text-xs leading-relaxed font-mono text-slate-700">
            {data.extracted_text}
          </pre>
          <button
            type="button"
            onClick={() => void downloadSource(requestId)}
            className="px-3 py-1.5 text-xs font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
          >
            {t("promotion.preview.download")}
          </button>
        </div>
      ) : (
        data.extracted_text.length > 0 && (
          <>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => void downloadSource(requestId)}
                className="text-xs text-sky-700 hover:underline"
              >
                {t("promotion.preview.download")}
              </button>
            </div>
            <pre className="whitespace-pre-wrap rounded bg-white p-4 text-xs leading-relaxed font-mono">
              {data.extracted_text}
            </pre>
          </>
        )
      )}
      {data.truncated && (
        <p className="mt-2 text-[10px] italic text-slate-500">
          {t("promotion.preview.truncated", { chunkCount: data.chunk_count })}
        </p>
      )}
    </div>
  );
}
